/**
 * @fileoverview System-web import-run detail page for reviewing and committing runs.
 * @module system-web/import-runs
 *
 * Responsibilities:
 * - Load a single import run from the local-first system cache or tenant service boundary.
 * - Render run status, summary, tenant context, and commit actions.
 * - Render partial failed-run summaries without throwing while sync catches up.
 *
 * Dependencies:
 * - systemImportRunService
 * - tenantService
 * - @firesquirrel/local-db import run contracts
 *
 * Architecture:
 * - Dexie-backed services remain the UI data source; online commit and refresh actions stay behind service functions.
 * - Summary rendering normalizes missing nested buckets from failed or in-progress runs.
 *
 * Notes:
 * - Import upload, commit, cancel, and refresh are permitted online exceptions for large-file workflows.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import type { ImportRun, ImportRunStatus, ImportRunSummary } from '@firesquirrel/local-db';
import { getSystemDb } from '@firesquirrel/local-db';
import { getImportRun, refreshImportRun, commitImportRun, isImportRunActive } from '../services/systemImportRunService';
import { getTenant } from '../services/tenantService';

const STATUS_DISPLAY: Record<ImportRunStatus, { label: string; className: string }> = {
  draft:             { label: 'Draft',            className: 'bg-slate-100 text-slate-700' },
  uploading:         { label: 'Uploading',        className: 'bg-sky-100 text-sky-700' },
  queued:            { label: 'Queued',           className: 'bg-gray-100 text-gray-700' },
  parsing:           { label: 'Parsing',          className: 'bg-blue-100 text-blue-700' },
  parsed:            { label: 'Preview Ready',    className: 'bg-indigo-100 text-indigo-700' },
  'dry-run':         { label: 'Dry Run',          className: 'bg-purple-100 text-purple-700' },
  'awaiting-commit': { label: 'Awaiting Commit',  className: 'bg-yellow-100 text-yellow-800' },
  committing:        { label: 'Committing',       className: 'bg-blue-100 text-blue-700' },
  finalizing:        { label: 'Finalizing',       className: 'bg-teal-100 text-teal-700' },
  completed:         { label: 'Completed',        className: 'bg-emerald-100 text-emerald-700' },
  failed:            { label: 'Failed',           className: 'bg-red-100 text-red-700' },
  cancelled:         { label: 'Cancelled',        className: 'bg-gray-100 text-gray-500' },
};

const SUMMARY_TILE_STYLES: Record<string, { container: string; value: string; label: string }> = {
  gray:   { container: 'bg-gray-50 border border-gray-200',     value: 'text-gray-700',   label: 'text-gray-600' },
  green:  { container: 'bg-green-50 border border-green-200',   value: 'text-green-700',  label: 'text-green-600' },
  blue:   { container: 'bg-blue-50 border border-blue-200',     value: 'text-blue-700',   label: 'text-blue-600' },
  yellow: { container: 'bg-yellow-50 border border-yellow-200', value: 'text-yellow-700', label: 'text-yellow-600' },
  red:    { container: 'bg-red-50 border border-red-200',       value: 'text-red-700',    label: 'text-red-600' },
};

function fmt(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * @process getSummaryTotals
 *
 * Steps:
 * 1. Read canonical import-run summary counters from a Dexie-backed run record.
 * 2. Default missing nested bucket values to zero for failed or partially synced runs.
 *
 * Inputs:
 * - summary: optional ImportRunSummary from the local-first import run projection.
 *
 * Outputs:
 * - totals used by summary tiles.
 *
 * Failure Handling:
 * - Missing summary or nested counters are treated as zero.
 *
 * Idempotency:
 * - REQUIRED.
 */
function getSummaryTotals(summary: Partial<ImportRunSummary> | undefined): {
  totalRows: number;
  created: number;
  updated: number;
  excludedTotal: number;
  failedTotal: number;
} {
  return {
    totalRows: summary?.totalRows ?? 0,
    created: summary?.created ?? 0,
    updated: summary?.updated ?? 0,
    excludedTotal:
      (summary?.excluded?.outOfJurisdiction ?? 0) +
      (summary?.excluded?.missingCoordinates ?? 0) +
      (summary?.excluded?.duplicateInsertOnly ?? 0) +
      (summary?.excluded?.missingMatchKey ?? 0),
    failedTotal:
      (summary?.failedByKind?.transform ?? 0) +
      (summary?.failedByKind?.entityBuild ?? 0),
  };
}

/**
 * @component ImportRunDetailPage
 *
 * Purpose:
 * - Display one local-first system import run and its operational status.
 *
 * Props:
 * - None. Route params provide the run identifier and query params provide tenant context.
 *
 * Behavior:
 * - Loads the run through service functions and renders partial summaries defensively.
 *
 * Data Source:
 * - local-first Dexie-backed services.
 *
 * Constraints:
 * - Does not call API endpoints directly; online commit/refresh exceptions stay behind services.
 *
 * Performance:
 * - Polls only active runs and clears the timer on unmount.
 */
export default function ImportRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();
  const [run, setRun] = useState<ImportRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantLabel, setTenantLabel] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string>('');
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!runId) { setLoading(false); setError('Missing run ID.'); return; }

    setLoading(true);
    setError(null);

    let resolvedTenantId = searchParams.get('tenantId') || '';
    let localRun: ImportRun | null = null;

    if (!resolvedTenantId) {
      const db = await getSystemDb();
      localRun = (await db.import_runs.get(runId) as ImportRun | null) ?? null;
      resolvedTenantId = (localRun as any)?.tenantId || '';
    }

    let label: string | null = 'System Data';
    if (resolvedTenantId) {
      try {
        const tenant = await getTenant(resolvedTenantId);
        if (tenant) {
          label = (tenant as any).name || (tenant as any).slug || (tenant as any)._id || null;
        } else {
          label = resolvedTenantId;
        }
      } catch {
        label = resolvedTenantId;
      }
    }

    setTenantLabel(label);
    setTenantId(resolvedTenantId);

    const r = await getImportRun(resolvedTenantId, runId);
    setRun(r);
  }, [runId, searchParams]);

  useEffect(() => {
    let cancelled = false;

    load()
      .catch((e: any) => {
        if (!cancelled) setError(e.message ?? 'Failed to load run.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [load]);

  useEffect(() => {
    if (!runId) return;
    if (!isImportRunActive(run)) return;

    const timer = window.setInterval(() => {
      refreshImportRun(tenantId, runId)
        .then(setRun)
        .catch(() => { /* ignore transient refresh errors */ });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [tenantId, runId, run]);

  const statusDisplay = run ? (STATUS_DISPLAY[run.status] ?? { label: run.status, className: 'bg-gray-100 text-gray-600' }) : null;
  const summaryTotals = getSummaryTotals(run?.summary);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0 px-6 py-4 border-b bg-white flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Import Run Details</h1>
          <p className="text-sm text-gray-500 mt-0.5">System import run status and result.</p>
          {run && isImportRunActive(run) && (
            <p className="text-xs text-blue-500 mt-0.5">This import is still active. Progress will resume updating automatically.</p>
          )}
          {tenantLabel && (
            <p className="text-xs text-gray-400 mt-0.5">
              Department: <span className="font-mono text-gray-500">{tenantLabel}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {run?.status === 'awaiting-commit' && tenantId && (
            <button
              type="button"
              disabled={committing}
              onClick={async () => {
                if (!tenantId || !runId) return;
                setCommitting(true);
                setCommitError(null);
                try {
                  await commitImportRun(tenantId, runId);
                  const refreshed = await getImportRun(tenantId, runId);
                  setRun(refreshed);
                } catch (e: any) {
                  setCommitError(e.message ?? 'Commit failed');
                } finally {
                  setCommitting(false);
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50">
              <CheckCircle className="h-3.5 w-3.5" />
              {committing ? 'Committing…' : 'Commit Import'}
            </button>
          )}
          {commitError && <span className="text-red-600 text-xs">{commitError}</span>}
          <Link to="/import-runs" className="text-blue-600 hover:text-blue-700 hover:underline">← Runs</Link>
          <Link to="/import-templates" className="text-gray-500 hover:text-gray-700 hover:underline">Templates</Link>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading && <div className="text-sm text-gray-400">Loading…</div>}
        {!loading && error && <div className="text-sm text-red-600">{error}</div>}
        {run && (
          <div className="max-w-2xl space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {(run as any).importName ?? run.filename}
                </h2>
                {(run as any).importDescription && (
                  <p className="text-sm text-gray-500 mt-0.5">{(run as any).importDescription}</p>
                )}
                <p className="text-xs text-gray-400 mt-1 font-mono">{run.filename}</p>
              </div>
              {statusDisplay && (
                <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusDisplay.className}`}>
                  {statusDisplay.label}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              {([
                ['Started',   fmt(run.startedAt)],
                ['Completed', fmt(run.completedAt)],
                ['Type',      run.isDryRun ? 'Dry Run' : 'Real Run'],
                ['Run ID',    run._id],
                ['Template',  (run as any).templateId ?? '—'],
                ['Started By', (run as any).startedBy ?? '—'],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                  <div className="text-gray-500 font-medium mb-0.5">{label}</div>
                  <div className="text-gray-800 font-mono break-all">{value || '—'}</div>
                </div>
              ))}
            </div>

            {run.errorMessage && (
              <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-sm text-red-700">
                {run.errorMessage}
              </div>
            )}

            {run.summary && (
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <h3 className="text-xs font-semibold text-gray-700 mb-3">Result Summary</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-center">
                  {([
                    ['Total',    summaryTotals.totalRows,     'gray'],
                    ['Created',  summaryTotals.created,       'green'],
                    ['Updated',  summaryTotals.updated,       'blue'],
                    ['Excluded', summaryTotals.excludedTotal, 'yellow'],
                    ['Errors',   summaryTotals.failedTotal,   'red'],
                  ] as [string, number, string][]).map(([label, value, color]) => {
                    const styles = SUMMARY_TILE_STYLES[color] ?? SUMMARY_TILE_STYLES.gray;
                    return (
                      <div key={label} className={`${styles.container} rounded-lg p-2`}>
                        <div className={`text-xl font-bold ${styles.value}`}>{value.toLocaleString()}</div>
                        <div className={`text-[11px] ${styles.label} mt-0.5`}>{label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {run.summary?.errors && run.summary.errors.length > 0 && (
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-xs font-semibold text-red-700">
                  Row Errors ({run.summary.errors.length})
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="min-w-full text-xs text-left">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 font-medium text-gray-700 w-12">Row</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Field</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Message</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Raw Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.summary.errors.map((e, i) => (
                        <tr key={i} className={i % 2 === 1 ? 'bg-gray-50/60 border-t' : 'border-t'}>
                          <td className="px-3 py-1.5 text-gray-500">{e.row}</td>
                          <td className="px-3 py-1.5 font-mono text-gray-700">{e.field ?? '—'}</td>
                          <td className="px-3 py-1.5 text-red-700">{e.message}</td>
                          <td className="px-3 py-1.5 font-mono text-gray-500 max-w-[180px] truncate">{e.rawValue ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(run as any).errorExportUrl && (
              <a href={(run as any).errorExportUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline border border-blue-200 rounded-md px-3 py-1.5 bg-blue-50 hover:bg-blue-100 transition-colors">
                Download error rows (CSV)
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
